/** @jsxRuntime classic */
/** @jsx jsx */

import icon_play from "../../designs/images/icon_video_play.svg";
import { loadCurrentTimeFromSessionStorage } from "../../utility/streaming";
import { getUseTHEOplayerV2FromLocalStorage } from "./enableTheoPlayerV2";
import { getHasUserActivation } from "./getHasUserActivation";
import { attachInterceptorToTHEOplayer } from "./theoplayer";
import {
  getTheoPlayerImportSource,
  getTHEOplayerRuntimeWithLibraryLocation,
} from "./videojsComponent/theoplayerRuntime";

import type { THEOSetting } from "@spwn/types/firebase/firestore";
import type { StreamingType } from "modules/streaming";

export const getTHEOplayerConfig = (): {
  license: string;
  libraryLocation: string;
} => {
  if (getUseTHEOplayerV2FromLocalStorage()) {
    // theoplayer v2.92.0のconfig
    return {
      license:
        "sZP7IYe6T6P6IQIlCLCrIOklIQ0zFSar0la-TuC_TmzZIueo3KfzTSXl0Sa6FOPlUY3zWokgbgjNIOf9fKPeCD0rCKCZFS113lC-3uCiCZzr3uxeFSRZ0Lh_3LIl0QhkCZfVfK4_bQgZCYxNWoryIQXzImf90SbrTuCZ3uRi0u5i0Oi6Io4pIYP1UQgqWgjeCYxgflEc3LCz0SCk3lak0Lh_FOPeWok1dDrLYtA1Ioh6TgV6v6fVfKcqCoXVdQjLUOfVfGxEIDjiWQXrIYfpCoj-fgzVfKxqWDXNWG3ybojkbK3gflNWf6E6FOPVWo31WQ1qbta6FOPzdQ4qbQc1sD4ZFK3qWmPUFOPLIQ-LflNWfK1zWDikfgzVfG3gWKxydDkibK4LbogqW6f9UwPkImi6IK41Uw4ZIY06Tg-Uya",
      libraryLocation: "https://cdn.jsdelivr.net/npm/theoplayer@2.92.0/",
    };
  }

  // theoplayer v8.14.0のconfig
  return {
    license:
      "sZP7IYe6T6P13LB_0S0iC6zz3QX6FSxKTQB-CS5o0mzrTD460LfL3S0t3oB6FOPlUY3zWokgbgjNIOf9fKPeCD0rCKCZFS113lC-3uCiCZzr3uxeFSRZ0Lh_3LIl0QhkCZfVfK4_bQgZCYxNWoryIQXzImf90SbrTuCZ3uRi0u5i0Oi6Io4pIYP1UQgqWgjeCYxgflEc3LRr0uecTuBc0lf_FOPeWok1dDrLYtA1Ioh6TgV6v6fVfKxqWQPrFK3qWmfVfKcqCoXVdQjLUOfVfG3pID49sY3iCYPZWtbpCoj-f6i6UQ1gWZrVdYIgf6i6UQ1gWtAVCYggb6rlWoz6Ymi6IQj-CDgpbkjLWt4ZCoh6TgV6v6fVfKxqWQPrFK3qWmfVfKcqCoXVdQjLUOfVfG3pID49sY3iCYPZWtbpCoj-f6i6UQ1gWZrVdYIgf6i6UQ1gWtAVCYggb6rlWoz6Ymi6box7bZf9DZPEUQkV3mPUFOPLIDreYog-bwPgbt3NWo_6TGxZUDhVfKIgCYxkbK4LflNWYYz",
    libraryLocation: "https://public.spwn.jp/packages/THEOplayer-8.14.0",
  };
};

/**
 * playerを作成する
 * @see https://docs.theoplayer.com/api-reference/web/theoplayer.playerconfiguration.md
 */
export const createPlayer = async (
  poster: string,
  // player作成後に、sourceを更新するケースがある
  url: string | "" | null,
  setting: THEOSetting,
  streamingType: StreamingType,
  vid: string | null = null
) => {
  const { THEOplayer: THEOplayerFromWindow } = window;
  const importSource = getTheoPlayerImportSource();
  const { libraryLocation, license } = getTHEOplayerConfig();
  const resolved =
    importSource === "node-module"
      ? await getTHEOplayerRuntimeWithLibraryLocation()
      : {
          THEOplayer: THEOplayerFromWindow,
          libraryLocation,
        };
  const { THEOplayer, libraryLocation: resolvedLibraryLocation } = resolved;

  const element = document.querySelector(".theoplayer-container");

  // @ts-expect-error TS2345
  const player = new THEOplayer.Player(element, {
    allowNativeFullscreen: setting.allowNativeFullscreen,
    // initialRendition: 'low',
    /**
     * https://portal.theoplayer.com/app/bdac9bf2-8a66-460c-944d-8235876c0e5c/players/808556fc-fb19-4fd7-8633-20184fda147e
     * 有効期限: 2026-12-30 (UTC) まで再生可、2026-12-31 00:00 UTC 以降は失効 (2026-09-11 に Date を偽装して実測)
     * ドメイン制限なし。CDN のポータルビルド (public.spwn.jp/packages/THEOplayer-x.y.z) はライセンスが
     * 埋め込み済みでこの値を参照しないため、期限更新時はビルドの再作成・再アップロードも必要
     */
    license,
    libraryLocation: resolvedLibraryLocation,
    mutedAutoplay: "all",
    ui: {
      aspectRatio: "16:9",
      playbackRates: setting.playBackRates,
    },
    allowMixedContent: true,
    pip: setting.enablePiP
      ? {
          visibility: 0.5,
          position: "bottom-right",
          retainPresentationModeOnSourceChange: true,
        }
      : {},
  });

  // player.src = 'https://wowza.peer5.com/live/smil:bbb_abr.smil/playlist.m3u8';
  player.abr.targetBuffer = setting.abr.targetBuffer;
  player.abr.strategy = setting.abr.strategy;
  player.muted = false; // default is unmuted
  // npmモジュール版では自動再生時にミュートが必要な場合に自動でミュートする機能が動かないため、userActivationを確認してミュート状態を設定する
  // TODO: npmモジュールへの移行が完了したらフィーチャーフラグと合わせて削除する
  if (importSource === "node-module") {
    player.muted = !getHasUserActivation();
  }
  player.autoplay = true;

  // player.poster = poster;

  player.preload = "auto";
  if (url !== null && url !== "") {
    player.source = {
      sources: [
        {
          src: url,
          type: "application/x-mpegurl",
          lowLatency: true,
          liveOffset: setting.initialLiveOffset,
          crossOrigin: "use-credentials",
        },
      ],
    };

    if (streamingType === "VOD" && !!vid) {
      const previousPlayHeadPosition = loadCurrentTimeFromSessionStorage(vid);
      if (previousPlayHeadPosition) {
        player.currentTime = previousPlayHeadPosition;
      }
    }
  }

  // THEOplayerの再生ボタンを変更する
  const playButtonContainer = document.getElementsByClassName(
    "theo-big-play-button-svg-container"
  );
  // @ts-expect-error TS2532
  playButtonContainer[0].innerHTML = "<img src=" + icon_play + ' alt=""/>';

  attachInterceptorToTHEOplayer({
    player,
    streamingType,
    videoId: vid ?? "",
    isLLHLSUsed: false,
  });

  return player;
};
